import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoIcon from '@mui/icons-material/Info';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { standardFieldLabelSx } from '../../components/common/formLabelStyles';
import { getConfigCrudDefinition } from './configCrudDefinitions';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import { HCP_MODEL_SEED, DRUG_MODEL_SEED } from './configurationSeeds';

const COMPARATOR_OPTIONS = [
  { value: 'Regex Patterns', label: 'Regex Patterns' },
  { value: 'Exists', label: 'Exists' },
  { value: 'Greater Than', label: 'Greater Than' },
  { value: 'Lesser Than', label: 'Lesser Than' },
  { value: 'Equals (Case Sensitive)', label: 'Equals (Case Sensitive)' },
  { value: 'Contains', label: 'Contains' },
  { value: 'Starts With', label: 'Starts With' },
  { value: 'List Equals', label: 'List Equals' },
  { value: 'Missing', label: 'Missing' },
  { value: 'In', label: 'In' },
  { value: 'Range', label: 'Range' },
];

const LOGICAL_OPERATORS = [
  { value: 'And', label: 'And' },
  { value: 'And Not', label: 'And Not' },
  { value: 'Or', label: 'Or' },
  { value: 'Or Not', label: 'Or Not' },
];

const VALIDATION_EVENTS = ['Create & Update Profile', 'Search Profile', 'Merge Profile'];

const cardSx = {
  minHeight: 300,
  p: 4,
  borderRadius: 3,
  border: '1px solid #dfe3ea',
  boxShadow: 'none',
  backgroundColor: '#fff',
};

const pageHeaderSx = {
  px: 2.25,
  py: 1.5,
  borderBottom: '1px solid #E5E7EB',
  borderLeft: '4px solid #1E88E5',
  display: 'flex',
  backgroundColor: '#fbfdfe',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  mb: 3,
};

const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 38,
    minHeight: 38,
    '& .MuiOutlinedInput-input': {
      padding: '10px 12px',
    },
  },
};

const fieldBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'flex-start',
};

const switchBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
};

const sectionHeaderSx = {
  fontWeight: 600,
  color: '#111827',
  mb: 2.5,
  pb: 1,
  borderBottom: '1px solid #f3f4f6',
};

const backButtonSx = {
  color: '#111827',
  backgroundColor: '#f3f4f6',
  width: 36,
  height: 36,
  '&:hover': {
    backgroundColor: '#e5e7eb',
  },
};

const primaryButtonSx = {
  minWidth: 100,
  px: 3,
  py: 0.8,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 2,
  backgroundColor: '#2196f3',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#1d88e5',
    boxShadow: 'none',
  },
};

const secondaryButtonSx = {
  minWidth: 100,
  px: 3,
  py: 0.8,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 2,
  color: '#374151',
  borderColor: '#d1d5db',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#f9fafb',
    borderColor: '#9ca3af',
  },
};

const createBuilderRow = () => ({
  logicalOperator: 'And',
  attribute: '',
  comparator: '',
  value: '',
  start: '',
  end: '',
});

const validateForm = (values) => {
  const errors = {};

  if (!values.name) errors.name = 'Attribute is required.';
  if (!values.displayName?.trim()) errors.displayName = 'Name is required.';
  if (!values.validationLevel) errors.validationLevel = 'Validation level is required.';
  if (!values.validationEvent) errors.validationEvent = 'Validation event is required.';
  if (!values.warningMessage?.trim()) errors.warningMessage = 'Warning message is required.';

  const rowErrors = values.builderRows.map((row) => {
    const current = {};
    if (!row.attribute) current.attribute = 'Attribute is required.';
    if (!row.comparator) current.comparator = 'Operator is required.';

    if (row.comparator === 'Range') {
      if (!row.start?.trim()) current.start = 'Start is required.';
      if (!row.end?.trim()) current.end = 'End is required.';
    } else if (!['Exists', 'Missing'].includes(row.comparator)) {
      if (!row.value?.trim()) current.value = 'Condition is required.';
    }

    return current;
  });

  if (rowErrors.some((row) => Object.keys(row).length > 0)) {
    errors.builderRows = rowErrors;
  }

  return errors;
};

const hasErrors = (errors) => {
  if (!errors || typeof errors !== 'object') return false;
  return Object.values(errors).some((value) => {
    if (Array.isArray(value)) {
      return value.some((entry) => entry && Object.keys(entry).length > 0);
    }
    return Boolean(value);
  });
};

const ValidationWorkspacePage = ({ entityType: entityTypeProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { entityType: entityTypeParam, mode, itemKey } = useParams();
  const entityType = entityTypeProp || entityTypeParam;
  const definition = getConfigCrudDefinition(entityType, 'validation-rules');

  const [localError, setLocalError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    name: '', // Maps to Attribute select in old UI
    displayName: '', // Maps to Name input in old UI
    description: '',
    active: '',
    validationLevel: '',
    validationEvent: '',
    warningMessage: '',
    builderRows: [createBuilderRow()],
  });

  const modelSeed = entityType === 'drug' ? DRUG_MODEL_SEED : HCP_MODEL_SEED;
  const attributeOptions = useMemo(() => {
    const opts = new Set(modelSeed.map((m) => m.displayName || m.name));
    if (form.name) opts.add(form.name);
    if (Array.isArray(form.builderRows)) {
      form.builderRows.forEach(row => {
        if (row.attribute) opts.add(row.attribute);
      });
    }
    return Array.from(opts);
  }, [modelSeed, form.name, form.builderRows]);

  const isEditMode = mode === 'edit';
  const backPath = `/configuration/${entityType}/validation-rules`;

  useEffect(() => {
    if (!definition) return;

    const cached = localStorage.getItem(definition.storageKey);
    let rows = [];
    try {
      rows = cached ? JSON.parse(cached) : [];
    } catch {
      rows = [];
    }

    if (isEditMode) {
      const stateRow = location.state?.row;
      // In old UI/Seed, name is the attribute. itemKey might be encoded name.
      const matchedRow =
        stateRow || rows.find((row) => String(row.name) === decodeURIComponent(itemKey || ''));

      if (matchedRow) {
        setForm({
          name: matchedRow.name || '',
          displayName: matchedRow.displayName || matchedRow.name || '',
          description: matchedRow.description || '',
          active: matchedRow.active ?? true,
          validationLevel: matchedRow.validationLevel || '',
          validationEvent: matchedRow.validationEvent || matchedRow.ValidationEvent || '',
          warningMessage:
            matchedRow.warningMessage || matchedRow.message || matchedRow.description || '',
          builderRows: matchedRow.builderRows || [
            {
              ...createBuilderRow(),
              attribute: matchedRow.name || '',
              value: matchedRow.rule || '',
              comparator: matchedRow.type || '',
            },
          ],
        });
      }
    }
  }, [definition, isEditMode, itemKey, location.state]);

  const onChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setLocalError('');
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const updateBuilderRow = (index, field, value) => {
    setForm((prev) => {
      const nextRows = [...prev.builderRows];
      nextRows[index] = { ...nextRows[index], [field]: value };
      return { ...prev, builderRows: nextRows };
    });
    setLocalError('');
    setFieldErrors((prev) => {
      if (!Array.isArray(prev.builderRows)) return prev;
      const nextBuilderErrors = [...prev.builderRows];
      nextBuilderErrors[index] = { ...(nextBuilderErrors[index] || {}), [field]: '' };
      return { ...prev, builderRows: nextBuilderErrors };
    });
  };

  const addMoreConditions = () => {
    setForm((prev) => ({ ...prev, builderRows: [...prev.builderRows, createBuilderRow()] }));
  };

  const removeCondition = (index) => {
    setForm((prev) => {
      const nextRows = prev.builderRows.filter((_, i) => i !== index);
      return { ...prev, builderRows: nextRows.length > 0 ? nextRows : [createBuilderRow()] };
    });
  };

  const onSave = () => {
    const errors = validateForm(form);
    setFieldErrors(errors);

    if (hasErrors(errors)) {
      setLocalError('Please fix the validation errors before saving.');
      return;
    }

    const cached = localStorage.getItem(definition.storageKey);
    let rows = [];
    try {
      rows = cached ? JSON.parse(cached) : [];
    } catch {
      rows = [];
    }

    const nextRow = {
      ...form,
      // For table display compatibility
      rule:
        form.builderRows[0]?.comparator === 'Regex Patterns'
          ? form.builderRows[0]?.value
          : form.builderRows[0]?.comparator,
      type: form.builderRows[0]?.comparator || 'Regex Patterns',
      lastModifiedDate: new Date().toLocaleDateString('en-GB'),
    };

    let nextRows;
    if (isEditMode) {
      const currentKey = decodeURIComponent(itemKey || '');
      nextRows = rows.map((row) =>
        String(row.name) === currentKey ? { ...row, ...nextRow } : row
      );
    } else {
      nextRows = [...rows, nextRow];
    }

    localStorage.setItem(definition.storageKey, JSON.stringify(nextRows));
    localStorage.setItem(`${definition.storageKey}__version`, definition.storageVersion);
    navigate(backPath);
  };

  if (!definition) {
    return <Alert severity="error">Configuration definition not found.</Alert>;
  }

  return (
    <Box sx={{ px: 1, pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PageBreadcrumbs
          items={[
            // { label: 'Configuration', path: `/configuration/${entityType}/model` },
            { label: 'Validation Rules', path: backPath },
            { label: isEditMode ? 'Edit Validation Rule' : 'Create Validation Rule' },
          ]}
        />
      </Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: 'none',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        <Box sx={pageHeaderSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: '#eaf4ff',
                color: '#1E88E5',
                display: 'flex',
              }}
            >
              <AutoFixHighIcon fontSize="small" />
            </Box> */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: '#1E293B', mb: 0.25, lineHeight: 1.2 }}
              >
                {isEditMode ? 'Edit Validation Rule' : 'Create Validation Rule'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                {isEditMode
                  ? 'Update the details for this record.'
                  : 'Configure and save a new validation rule.'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <Box sx={fieldBlockSx}>
                <Typography sx={standardFieldLabelSx}>
                  Attributes{' '}
                  <Box component="span" sx={{ color: '#d32f2f' }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  sx={inputFieldSx}
                  value={form.name}
                  onChange={(e) => onChange('name', e.target.value)}
                  error={Boolean(fieldErrors.name)}
                  helperText={fieldErrors.name || ''}
                  placeholder="Select Attribute"
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    Select Attribute
                  </MenuItem>
                  {attributeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography sx={{ mt: 0.5, color: '#6b7280' }}>
                  The attribute this function will validate
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Box sx={fieldBlockSx}>
                <Typography sx={standardFieldLabelSx}>
                  Name{' '}
                  <Box component="span" sx={{ color: '#d32f2f' }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  sx={inputFieldSx}
                  value={form.displayName}
                  onChange={(e) => onChange('displayName', e.target.value)}
                  error={Boolean(fieldErrors.displayName)}
                  helperText={fieldErrors.displayName || ''}
                  placeholder="Short Name"
                />
                <Typography sx={{ mt: 0.5, color: '#6b7280' }}>Name Displayed in MDM UI</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Box sx={fieldBlockSx}>
                <Typography sx={standardFieldLabelSx}>Description</Typography>
                <TextField
                  fullWidth
                  size="small"
                  sx={inputFieldSx}
                  value={form.description}
                  onChange={(e) => onChange('description', e.target.value)}
                  placeholder="Enter description"
                />
                <Typography sx={{ mt: 0.5, color: '#6b7280' }}>
                  Optional. Descriptive information about this attribute
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Box sx={switchBlockSx}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography sx={{ ...standardFieldLabelSx, mb: 0 }}>Active</Typography>
                  <Switch
                    checked={form.active}
                    onChange={(e) => onChange('active', e.target.checked)}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Typography sx={{ color: '#6b7280' }}>
                  Turn off if you don't want this function applied to you data
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Box sx={fieldBlockSx}>
                <Typography sx={standardFieldLabelSx}>
                  Validation Level
                  <Tooltip
                    title="This defines the strictness of the validation rule."
                    placement="top"
                  >
                    <InfoIcon
                      sx={{ ml: 0.5, color: '#94a3b8', verticalAlign: 'middle', cursor: 'pointer' }}
                    />
                  </Tooltip>
                  <Box component="span" sx={{ color: '#d32f2f' }}>
                    {' '}
                    *
                  </Box>
                </Typography>
                <RadioGroup
                  row
                  value={form.validationLevel}
                  onChange={(e) => onChange('validationLevel', e.target.value)}
                  sx={{ gap: 2, mt: 0.5 }}
                >
                  <FormControlLabel value="Error" control={<Radio size="small" />} label="Error" />
                  <FormControlLabel
                    value="Warning"
                    control={<Radio size="small" />}
                    label="Warning"
                  />
                </RadioGroup>
                {fieldErrors.validationLevel && (
                  <Typography sx={{ mt: 0.5, color: '#d32f2f' }}>
                    {fieldErrors.validationLevel}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Box sx={fieldBlockSx}>
                <Typography sx={standardFieldLabelSx}>
                  Validation Event{' '}
                  <Box component="span" sx={{ color: '#d32f2f' }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  sx={inputFieldSx}
                  value={form.validationEvent}
                  onChange={(e) => onChange('validationEvent', e.target.value)}
                  error={Boolean(fieldErrors.validationEvent)}
                  helperText={fieldErrors.validationEvent || ''}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    Select Validation Event
                  </MenuItem>
                  {VALIDATION_EVENTS.map((ev) => (
                    <MenuItem key={ev} value={ev}>
                      {ev}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <hr
              style={{
                backgroundColor: '#e5e7eb',
                border: 'none',
                height: '1px',
                marginBottom: '12px',
              }}
            />
            <Typography sx={{ fontWeight: 600, color: '#111827', mb: 1.5 }}>
              Function Builder
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ mb: 1.5, fontWeight: 600, color: '#374151' }}>
                If the following conditions are met
              </Typography>

              {form.builderRows.map((row, index) => (
                <Grid
                  container
                  spacing={2}
                  key={`row-${index}`}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Grid item xs={1.5}>
                    {index > 0 ? (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        sx={inputFieldSx}
                        value={row.logicalOperator}
                        onChange={(e) => updateBuilderRow(index, 'logicalOperator', e.target.value)}
                      >
                        <MenuItem value="" disabled>
                          Select Operator
                        </MenuItem>
                        {LOGICAL_OPERATORS.map((op) => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : null}
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      sx={inputFieldSx}
                      value={row.attribute}
                      onChange={(e) => updateBuilderRow(index, 'attribute', e.target.value)}
                      error={Boolean(fieldErrors.builderRows?.[index]?.attribute)}
                      helperText={fieldErrors.builderRows?.[index]?.attribute || ''}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="">Select Attribute</MenuItem>
                      {attributeOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      sx={inputFieldSx}
                      value={row.comparator}
                      onChange={(e) => updateBuilderRow(index, 'comparator', e.target.value)}
                      error={Boolean(fieldErrors.builderRows?.[index]?.comparator)}
                      helperText={fieldErrors.builderRows?.[index]?.comparator || ''}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="" disabled>
                        Select Operator
                      </MenuItem>
                      {COMPARATOR_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={3.5}>
                    {row.comparator === 'Range' ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          sx={inputFieldSx}
                          placeholder="Start"
                          value={row.start}
                          onChange={(e) => updateBuilderRow(index, 'start', e.target.value)}
                          error={Boolean(fieldErrors.builderRows?.[index]?.start)}
                          helperText={fieldErrors.builderRows?.[index]?.start || ''}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          sx={inputFieldSx}
                          placeholder="End"
                          value={row.end}
                          onChange={(e) => updateBuilderRow(index, 'end', e.target.value)}
                          error={Boolean(fieldErrors.builderRows?.[index]?.end)}
                          helperText={fieldErrors.builderRows?.[index]?.end || ''}
                        />
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        sx={inputFieldSx}
                        placeholder="Enter condition"
                        value={row.value}
                        onChange={(e) => updateBuilderRow(index, 'value', e.target.value)}
                        disabled={['Exists', 'Missing'].includes(row.comparator)}
                        error={Boolean(fieldErrors.builderRows?.[index]?.value)}
                        helperText={fieldErrors.builderRows?.[index]?.value || ''}
                      />
                    )}
                  </Grid>

                  <Grid item xs={1}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {form.builderRows.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeCondition(index)}
                          sx={{ color: '#fc5a5a' }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      )}
                      {index === form.builderRows.length - 1 && (
                        <IconButton
                          size="small"
                          onClick={addMoreConditions}
                          sx={{ color: '#6161f9' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              ))}
            </Box>

            <Box sx={{ mt: 5 }}>
              <Typography sx={{ mb: 2.5, fontWeight: 600, color: '#111827' }}>Then</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Typography sx={{ whiteSpace: 'nowrap', color: '#111827' }}>
                      Display warning with message{' '}
                      <Box component="span" sx={{ color: '#d32f2f' }}>
                        *
                      </Box>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      sx={{ ...inputFieldSx, maxWidth: 640 }}
                      value={form.warningMessage}
                      onChange={(e) => onChange('warningMessage', e.target.value)}
                      error={Boolean(fieldErrors.warningMessage)}
                      helperText={fieldErrors.warningMessage || ''}
                      placeholder="Enter Warning Message"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 8 }}>
            <Button
              className="action-btn cancel-btn"
              variant="outlined"
              sx={secondaryButtonSx}
              onClick={() => navigate(backPath)}
            >
              Cancel
            </Button>
            <Button
              className="action-btn process-btn"
              variant="contained"
              sx={primaryButtonSx}
              onClick={onSave}
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

ValidationWorkspacePage.propTypes = {
  entityType: PropTypes.oneOf(['hcp', 'drug']),
};

export default ValidationWorkspacePage;
